"use client";

import type { ChangeEvent } from "react";
import { togglePillButtonClass } from "@/app/components/ui/toggle-pill";

export type HeadingItem = {
  id: string;
  level: number;
  title: string;
};

const TOC_EXPANDED_WIDTH = "clamp(13rem, 20vw, 19rem)";

export function MobileTableOfContents({
  currentHeadingId,
  headings,
  onNavigate,
  isPending = false,
  readableLabels = false,
}: {
  currentHeadingId: string | null;
  headings: HeadingItem[];
  onNavigate: (id: string) => void;
  isPending?: boolean;
  readableLabels?: boolean;
}) {
  if (headings.length === 0) {
    if (!isPending) return null;

    return (
      <nav
        aria-hidden
        className={`not-prose sticky top-[3.75rem] z-20 ${
          readableLabels ? "mb-6" : "mb-12"
        } rounded-b-lg border border-gray-200 border-t-0 bg-white/95 px-4 pb-4 pt-2 text-sm shadow-sm backdrop-blur-sm motion-safe:animate-pulse motion-reduce:animate-none xl:hidden`}
      >
        <div className="h-8 w-3/4 rounded bg-gray-200" />
      </nav>
    );
  }

  const currentHeading = currentHeadingId
    ? headings.find((heading) => heading.id === currentHeadingId)
    : undefined;

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    if (!value) return;
    onNavigate(value);
  };

  return (
    <nav
      aria-label="Article sections"
      data-lab-guide-toc={readableLabels ? "true" : undefined}
      className={`not-prose sticky top-[3.75rem] z-20 ${
        readableLabels ? "mb-6" : "mb-12"
      } rounded-b-lg border border-gray-200 border-t-0 bg-white/95 px-4 pb-4 pt-2 text-sm shadow-sm backdrop-blur-sm xl:hidden`}
    >
      <label
        className={
          readableLabels
            ? "mb-2 block text-sm font-semibold text-gray-700"
            : "mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600"
        }
        htmlFor="mobile-toc"
      >
        Jump to section
      </label>
      <select
        id="mobile-toc"
        className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
        onChange={handleChange}
        value={currentHeadingId ?? ""}
      >
        <option disabled value="">
          {currentHeading ? "Select another…" : "Jump to…"}
        </option>
        {headings.map((heading) => {
          const indent = " ".repeat(Math.max(0, heading.level - 1) * 2);
          return (
            <option key={heading.id} value={heading.id}>
              {`${indent}${heading.title}`}
            </option>
          );
        })}
      </select>
    </nav>
  );
}

export function DesktopTableOfContents({
  currentHeadingId,
  headings,
  isAutoCollapse,
  isCollapsed,
  onAutoToggle,
  onNavigate,
  onToggleCollapse,
  isPending = false,
  readableLabels = false,
}: {
  currentHeadingId: string | null;
  headings: HeadingItem[];
  isAutoCollapse: boolean;
  isCollapsed: boolean;
  onAutoToggle: (enabled: boolean) => void;
  onNavigate: (id: string) => void;
  onToggleCollapse: () => void;
  isPending?: boolean;
  readableLabels?: boolean;
}) {
  if (headings.length === 0) {
    if (!isPending) return null;

    return (
      <aside
        className="relative mt-12 hidden xl:block xl:shrink-0 xl:sticky xl:top-24 xl:self-start"
        style={{ width: TOC_EXPANDED_WIDTH, minWidth: "13rem" }}
      >
        <div
          aria-hidden
          className="rounded-lg border border-gray-200 bg-white p-5 text-sm shadow-sm motion-safe:animate-pulse motion-reduce:animate-none"
        >
          <div className="mb-3 h-3 w-24 rounded bg-gray-200" />
          <ul className="space-y-2">
            {[0, 1, 2, 3].map((index) => (
              <li key={index}>
                <div className="h-3 w-full rounded bg-gray-200" />
              </li>
            ))}
          </ul>
        </div>
      </aside>
    );
  }

  const navClasses = `rounded-lg border border-gray-200 bg-white p-5 text-sm shadow-sm transition-all duration-300 not-prose ${
    isCollapsed
      ? "translate-x-full opacity-0 pointer-events-none"
      : "translate-x-0 opacity-100"
  }`;

  const expandHandleClasses = `${
    readableLabels ? "normal-case text-sm" : "uppercase text-[11px]"
  } absolute top-28 -right-4 flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white font-semibold tracking-tight text-gray-600 shadow-sm transition-all duration-300 ${
    isCollapsed
      ? "translate-x-0 opacity-100"
      : "translate-x-6 opacity-0 pointer-events-none"
  }`;

  return (
    <aside
      className={`relative mt-12 hidden transition-[width] duration-300 ease-out xl:block xl:shrink-0 xl:sticky xl:top-24 xl:self-start ${
        isCollapsed ? "w-0" : ""
      }`}
      style={{
        width: isCollapsed ? 0 : TOC_EXPANDED_WIDTH,
        minWidth: isCollapsed ? 0 : "13rem",
      }}
    >
      <div
        aria-hidden={isCollapsed}
        className={navClasses}
        style={isCollapsed ? { display: "none" } : undefined}
      >
        <div
          className={
            readableLabels
              ? "mb-3 flex flex-wrap items-start justify-between gap-3 text-sm font-semibold text-gray-700"
              : "mb-3 flex items-start justify-between gap-3 text-xs font-semibold uppercase tracking-wide text-gray-600"
          }
        >
          <p>On this page</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-pressed={isAutoCollapse}
              className={togglePillButtonClass({
                active: isAutoCollapse,
                className: readableLabels
                  ? "px-2.5 py-1 text-xs font-medium transition-colors"
                  : "px-2.5 py-1 text-[11px] font-medium uppercase tracking-tight transition-colors",
                activeClassName:
                  "border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100",
                inactiveClassName:
                  "border-gray-300 bg-gray-100 text-gray-600 hover:bg-gray-200",
                ringClassName: "focus-visible:ring-blue-600",
              })}
              onClick={() => onAutoToggle(!isAutoCollapse)}
            >
              Auto collapse: {isAutoCollapse ? "On" : "Off"}
            </button>
            <button
              type="button"
              aria-expanded={!isCollapsed}
              className={
                readableLabels
                  ? "text-sm font-medium text-gray-600 hover:text-gray-800"
                  : "text-[11px] font-medium uppercase tracking-tight text-gray-500 hover:text-gray-700"
              }
              onClick={onToggleCollapse}
            >
              {isCollapsed ? "Expand" : "Hide"}
            </button>
          </div>
        </div>
        <div
          className="max-h-[70vh] overflow-y-auto pr-1"
          data-toc-container="desktop"
        >
          <ul className="space-y-2">
            {headings.map((heading) => (
              <li
                key={heading.id}
                style={{ marginLeft: (heading.level - 1) * 12 }}
              >
                <button
                  type="button"
                  aria-current={
                    currentHeadingId === heading.id ? "true" : undefined
                  }
                  className={`block w-full text-left rounded-sm px-2 py-1 text-sm transition-colors ${
                    currentHeadingId === heading.id
                      ? "bg-blue-50 font-semibold text-blue-800"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  data-heading-id={heading.id}
                  onClick={() => {
                    onNavigate(heading.id);
                  }}
                >
                  {heading.title}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <button
        type="button"
        aria-expanded={!isCollapsed}
        aria-hidden={!isCollapsed}
        aria-label={
          isCollapsed
            ? "Expand table of contents"
            : "Collapse table of contents"
        }
        className={expandHandleClasses}
        style={!isCollapsed ? { display: "none" } : undefined}
        onClick={onToggleCollapse}
      >
        <span className="sr-only">Toggle table of contents</span>
        {readableLabels ? "Show" : "TOC"}
      </button>
    </aside>
  );
}
